import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { KanbanColumn, CustomField } from '@/types';
import { supabase } from '@/lib/supabase';
import { fetchWithAuth } from '@/services/api';
import { useAuthStore } from '@/store/authStore';
import { useLojaAtivaStore } from '@/store/lojaAtivaStore';

interface ConfigState {
  kanbanColumns: KanbanColumn[];
  customFields: CustomField[];
  loading: boolean;
  initialized: boolean;
  customFieldsStatus: 'no_scope' | 'loading' | 'loaded' | 'error';
  customFieldsError: string | null;
  fetchConfigs: (force?: boolean) => Promise<void>;
  updateKanbanColumns: (columns: KanbanColumn[]) => Promise<void>;
  addKanbanColumn: (column: Partial<KanbanColumn>) => Promise<void>;
  updateKanbanColumn: (id: string, updates: Partial<KanbanColumn>) => Promise<void>;
  deleteKanbanColumn: (id: string) => Promise<void>;
  updateCustomFields: (fields: CustomField[], options?: { allowGlobalChanges?: boolean }) => Promise<void>;
}

const DEFAULT_FIELDS: CustomField[] = [];

function mapRowToColumn(d: any): KanbanColumn {
  const titulo = d.titulo || d.title || d.status_value || 'Etapa';
  const cor = d.cor || d.color || '#2563EB';
  const ordem = typeof d.ordem === 'number' ? d.ordem : (typeof d.order_index === 'number' ? d.order_index : 0);
  const ativa = d.ativa !== undefined ? Boolean(d.ativa) : (d.active !== undefined ? Boolean(d.active) : true);

  return {
    id: String(d.id),
    tenant_id: d.tenant_id || null,
    loja_id: d.loja_id || null,
    key: d.key || 'etapa_' + d.id,
    titulo,
    title: titulo,
    status_value: titulo,
    ordem,
    order_index: ordem,
    cor,
    color: cor,
    tipo: d.tipo || 'ativo',
    ativa,
    active: ativa,
    is_system: d.is_system === true,
    descricao_ia: d.descricao_ia !== undefined ? d.descricao_ia : null,
    created_at: d.created_at,
    updated_at: d.updated_at,
  };
}

// Operational scope comes from the accessible selected store, never admin profile tenant.
function selectedFieldsScope() {
  const state = useLojaAtivaStore.getState();
  const store = state.lojasAcessiveis.find(s => s.id === state.lojaAtivaId);
  return store?.tenant_id ? { tenantId: store.tenant_id, lojaId: store.id } : null;
}
let loadedFieldDefinitions: CustomField[] = [];
let loadedFieldsScope: string | null = null;
let loadedFieldsRevision: string | null = null;
let customFieldsSaving = false;
let customFieldsGeneration = 0;

export const useConfigStore = create<ConfigState>()(
  persist(
    (set, get) => ({
      kanbanColumns: [],
      customFields: DEFAULT_FIELDS,
      loading: false,
      initialized: false,
      customFieldsStatus: 'no_scope',
      customFieldsError: null,

  fetchConfigs: async (force = false) => {
    if ((get().initialized || get().loading) && !force) return;
    set({ loading: true, customFieldsStatus: selectedFieldsScope() ? 'loading' : 'no_scope', customFieldsError: null });
    const generation = customFieldsGeneration;
    try {
      const lojaAtivaId = useLojaAtivaStore.getState().lojaAtivaId;
      let fetchedCols: any[] | null = null;

      try {
        const queryParams = lojaAtivaId ? `?loja_id=${lojaAtivaId}` : '';
        let res = await fetchWithAuth(`/api/kanban/columns${queryParams}`);
        if (!res.ok) {
          res = await fetchWithAuth(`/api/etapas${queryParams}`);
        }
        if (res.ok) {
          fetchedCols = await res.json();
        }
      } catch (apiErr) {
        console.warn('[CONFIG_STORE] Error fetching via /api/kanban/columns, falling back to client Supabase:', apiErr);
      }

      if (fetchedCols && Array.isArray(fetchedCols)) {
        const columns = fetchedCols.map(mapRowToColumn);
        if (generation === customFieldsGeneration && useLojaAtivaStore.getState().lojaAtivaId === lojaAtivaId) set({ kanbanColumns: columns });
      } else if (supabase) {
        let query = supabase
          .from('crm_pipeline_etapas')
          .select('*')
          .order('ordem', { ascending: true });

        if (lojaAtivaId) {
          query = query.or(`loja_id.eq.${lojaAtivaId},loja_id.is.null`);
        }

        const { data, error } = await query;

        if (error) {
          console.error('[CONFIG_STORE] Error fetching crm_pipeline_etapas:', error);
        } else if (data) {
          const columns = data.map(mapRowToColumn);
          if (generation === customFieldsGeneration && useLojaAtivaStore.getState().lojaAtivaId === lojaAtivaId) set({ kanbanColumns: columns });
        }
      }

      const scope = selectedFieldsScope();
      if (!scope || !useAuthStore.getState().session?.user?.id) return;
      const response = await fetchWithAuth(`/api/leads/custom-field-definitions?loja_id=${encodeURIComponent(scope.lojaId)}`);
      const payload = await response.json();
      if (generation !== customFieldsGeneration) return;
      if (!response.ok) throw new Error(payload.error || 'Não foi possível carregar os campos personalizados.');
      if (payload.tenant_id !== scope.tenantId || payload.loja_id !== scope.lojaId || !Array.isArray(payload.fields) || typeof payload.revision !== 'string') throw new Error('Resposta de campos inválida. Tente novamente.');
      loadedFieldDefinitions = payload.fields;
      loadedFieldsScope = `${scope.tenantId}:${scope.lojaId}`;
      loadedFieldsRevision = payload.revision;
      set({ customFields: payload.fields, customFieldsStatus: 'loaded', customFieldsError: null, initialized: true });
    } catch (err) {
      if (generation === customFieldsGeneration) set({ initialized: false, customFieldsStatus: 'error', customFieldsError: err instanceof Error ? err.message : 'Não foi possível carregar os campos.' });
    } finally {
      if (generation === customFieldsGeneration) set({ loading: false });
    }
  },

  updateKanbanColumns: async (columns) => {
    const previous = get().kanbanColumns;
    set({ kanbanColumns: columns });

    const updates = columns.map((col, idx) => ({ id: col.id, ordem: idx }));

    try {
      const res = await fetchWithAuth('/api/etapas/reorder', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });
      if (!res.ok) {
        throw new Error('Erro ao reordenar etapas no servidor');
      }
    } catch (err) {
      console.warn('[CONFIG_STORE] API reorder failed, trying individual PATCH requests:', err);
      try {
        const results = await Promise.all(
          updates.map(item =>
            fetchWithAuth(`/api/etapas/${item.id}`, {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ ordem: item.ordem })
            })
          )
        );

        const failed = results.filter(r => !r.ok);
        if (failed.length > 0) {
          console.error('[CONFIG_STORE] Falha ao reordenar', failed.length, 'de', updates.length, 'etapas');
          set({ kanbanColumns: previous });
          throw new Error(`Não foi possível salvar a nova ordem (${failed.length} de ${updates.length} etapas falharam).`);
        }
      } catch (fallbackErr) {
        set({ kanbanColumns: previous });
        throw fallbackErr;
      }
    }
  },

  addKanbanColumn: async (column) => {
    const titulo = column.titulo || column.title || 'Nova Etapa';
    const cor = column.cor || column.color || '#2563EB';
    const key = column.key || titulo.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, '_');
    const ordem = get().kanbanColumns.length;
    const lojaAtivaId = useLojaAtivaStore.getState().lojaAtivaId;
    const profile = useAuthStore.getState().profile;
    const tenantId = column.tenant_id || profile?.tenant_id || null;

    const bodyPayload = {
      key,
      titulo,
      ordem,
      cor,
      tipo: column.tipo || 'ativo',
      descricao_ia: column.descricao_ia !== undefined ? column.descricao_ia : null,
      loja_id: column.loja_id !== undefined ? column.loja_id : lojaAtivaId,
      tenant_id: tenantId
    };

    let createdData: any = null;

    try {
      const res = await fetchWithAuth('/api/etapas', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bodyPayload)
      });

      if (res.ok) {
        createdData = await res.json();
      } else {
        const errJson = await res.json().catch(() => ({}));
        throw new Error(errJson.error || 'Erro ao criar etapa');
      }
    } catch (apiErr: any) {
      console.warn('[CONFIG_STORE] API create stage failed, attempting direct Supabase insert:', apiErr);
      if (supabase) {
        const insertPayload: any = {
          key,
          titulo,
          ordem,
          cor,
          tipo: column.tipo || 'ativo',
          ativa: true,
          is_system: false,
          descricao_ia: column.descricao_ia !== undefined ? column.descricao_ia : null,
          loja_id: column.loja_id !== undefined ? column.loja_id : lojaAtivaId,
        };
        if (tenantId) insertPayload.tenant_id = tenantId;

        const { data, error } = await supabase
          .from('crm_pipeline_etapas')
          .insert([insertPayload])
          .select('*')
          .single();

        if (error) {
          console.error('[CONFIG_STORE] Error creating etapa:', error);
          throw error;
        }
        createdData = data;
      } else {
        throw apiErr;
      }
    }

    if (createdData) {
      const newCol = mapRowToColumn(createdData);
      set(state => ({ kanbanColumns: [...state.kanbanColumns, newCol] }));
    }
  },

  updateKanbanColumn: async (id, updates) => {
    // Optimistic update
    set(state => ({
      kanbanColumns: state.kanbanColumns.map(c => {
        if (c.id === id) {
          const updated = { ...c, ...updates };
          if (updates.titulo) {
            updated.title = updates.titulo;
            updated.status_value = updates.titulo;
          }
          if (updates.cor) updated.color = updates.cor;
          if (updates.ativa !== undefined) updated.active = updates.ativa;
          if (updates.ordem !== undefined) updated.order_index = updates.ordem;
          if (updates.descricao_ia !== undefined) updated.descricao_ia = updates.descricao_ia;
          return updated;
        }
        return c;
      })
    }));

    try {
      const res = await fetchWithAuth(`/api/etapas/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      if (!res.ok) {
        const errJson = await res.json().catch(() => ({}));
        throw new Error(errJson.error || 'Erro ao atualizar etapa');
      }
    } catch (apiErr) {
      console.warn('[CONFIG_STORE] API update stage failed, attempting direct Supabase update:', apiErr);
      if (supabase) {
        const dbPayload: any = {};
        if (updates.titulo !== undefined) dbPayload.titulo = updates.titulo;
        if (updates.title !== undefined && dbPayload.titulo === undefined) dbPayload.titulo = updates.title;
        if (updates.cor !== undefined) dbPayload.cor = updates.cor;
        if (updates.color !== undefined && dbPayload.cor === undefined) dbPayload.cor = updates.color;
        if (updates.tipo !== undefined) dbPayload.tipo = updates.tipo;
        if (updates.ativa !== undefined) dbPayload.ativa = updates.ativa;
        if (updates.active !== undefined && dbPayload.ativa === undefined) dbPayload.ativa = updates.active;
        if (updates.ordem !== undefined) dbPayload.ordem = updates.ordem;
        if (updates.descricao_ia !== undefined) dbPayload.descricao_ia = updates.descricao_ia;

        const { error } = await supabase
          .from('crm_pipeline_etapas')
          .update(dbPayload)
          .eq('id', id);

        if (error) {
          console.error('[CONFIG_STORE] Error updating etapa:', error);
          get().fetchConfigs(true);
          throw error;
        }
      } else {
        get().fetchConfigs(true);
        throw apiErr;
      }
    }
  },

  deleteKanbanColumn: async (id) => {
    set(state => ({
      kanbanColumns: state.kanbanColumns.filter(c => c.id !== id)
    }));

    try {
      const res = await fetchWithAuth(`/api/etapas/${id}`, {
        method: 'DELETE'
      });
      if (!res.ok) {
        const errJson = await res.json().catch(() => ({}));
        throw new Error(errJson.error || 'Erro ao deletar etapa');
      }
    } catch (apiErr) {
      console.warn('[CONFIG_STORE] API delete stage failed, attempting direct Supabase delete:', apiErr);
      if (supabase) {
        const { error } = await supabase
          .from('crm_pipeline_etapas')
          .delete()
          .eq('id', id);

        if (error) {
          console.error('[CONFIG_STORE] Error deleting etapa:', error);
          get().fetchConfigs(true);
          throw error;
        }
      } else {
        get().fetchConfigs(true);
        throw apiErr;
      }
    }
  },

  updateCustomFields: async (fields, options) => {
    if (customFieldsSaving) throw new Error('Aguarde o salvamento em andamento.');
    const scope = selectedFieldsScope();
    const userId = useAuthStore.getState().session?.user?.id;
    if (!scope || !userId || !loadedFieldsRevision || loadedFieldsScope !== `${scope.tenantId}:${scope.lojaId}`) throw new Error('Carregue os campos da loja antes de salvar.');
    const generation = customFieldsGeneration;
    customFieldsSaving = true;
    try {
      const response = await fetchWithAuth('/api/leads/custom-field-definitions', {
        method: 'PUT', headers: { 'Content-Type': 'application/json', 'x-loja-id': scope.lojaId },
        body: JSON.stringify({ loja_id: scope.lojaId, fields, expected_revision: loadedFieldsRevision, allow_global_changes: options?.allowGlobalChanges === true })
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || 'Não foi possível confirmar o salvamento. Recarregue antes de repetir.');
      if (generation !== customFieldsGeneration || useAuthStore.getState().session?.user?.id !== userId) throw new Error('Sessão alterada durante o salvamento. Reabra a configuração.');
      if (payload.tenant_id !== scope.tenantId || payload.loja_id !== scope.lojaId || !Array.isArray(payload.fields) || typeof payload.revision !== 'string') throw new Error('Confirmação inválida. Recarregue os campos.');
      loadedFieldDefinitions = payload.fields;
      loadedFieldsRevision = payload.revision;
      set({ customFields: payload.fields });
    } finally {
      customFieldsSaving = false;
    }
  }
    }),
    {
      name: 'task-crm-config-cache',
      // Drop preexisting unscoped field caches, including caches from older versions.
      merge: (persisted, current) => ({ ...current, kanbanColumns: (persisted as any)?.kanbanColumns || [] }),
      partialize: (state) => ({
        kanbanColumns: state.kanbanColumns
      })
    }
  )
);

useLojaAtivaStore.subscribe((state, previous) => {
  const currentTenant = state.lojasAcessiveis.find(s => s.id === state.lojaAtivaId)?.tenant_id ?? null;
  const previousTenant = previous.lojasAcessiveis.find(s => s.id === previous.lojaAtivaId)?.tenant_id ?? null;
  if (state.lojaAtivaId !== previous.lojaAtivaId || currentTenant !== previousTenant) {
    loadedFieldDefinitions = [];
    loadedFieldsScope = null;
    loadedFieldsRevision = null;
    customFieldsGeneration++;
    useConfigStore.setState({ customFields: DEFAULT_FIELDS, initialized: false, loading: false, customFieldsStatus: 'no_scope', customFieldsError: null });
    if (selectedFieldsScope() && useAuthStore.getState().session?.user?.id) queueMicrotask(() => { void useConfigStore.getState().fetchConfigs(); });
  }
});
useAuthStore.subscribe((state, previous) => {
  if (state.session?.user?.id !== previous.session?.user?.id || state.profile?.tenant_id !== previous.profile?.tenant_id) {
    loadedFieldDefinitions = [];
    loadedFieldsScope = null;
    loadedFieldsRevision = null;
    customFieldsGeneration++;
    useConfigStore.setState({ customFields: DEFAULT_FIELDS, initialized: false, loading: false, customFieldsStatus: 'no_scope', customFieldsError: null });
    if (selectedFieldsScope() && useAuthStore.getState().session?.user?.id) queueMicrotask(() => { void useConfigStore.getState().fetchConfigs(); });
  }
});
